import { gql } from "@apollo/client";

export const COMPETITION_COMMENT_FIELDS = gql`
  fragment CompetitionCommentFields on Comment {
    id
    _id
    createdAt
    deletedAt
    text
    gifUrl
    user {
      _id
      avatarUrl: avatar
      username
      userGroup {
        type
      }
    }
  }
`;

export const GET_COMPETITION_COMMENT_COUNT = gql`
  query CompetitionCommentCount($id: String!) {
    competition(id: $id) {
      id
      comments(first: 0, excludeDeleted: true) {
        pageInfo {
          totalCount
        }
      }
    }
  }
`;

export const GET_COMPETITION_COMMENTS = gql`
  ${COMPETITION_COMMENT_FIELDS}

  query CompetitionComments($id: String!, $first: Int, $after: String) {
    competition(id: $id) {
      id
      comments(first: $first, after: $after, excludeDeleted: true) {
        pageInfo {
          endCursor
          hasNextPage
          totalCount
        }
        nodes {
          ...CompetitionCommentFields
        }
      }
    }
  }
`;

export const COMPETITION_COMMENT_CREATE = gql`
  ${COMPETITION_COMMENT_FIELDS}

  mutation CompetitionCommentCreate(
    $commentableId: String!
    $text: String!
    $gifUrl: String
  ) {
    commentCreate(
      input: { commentableId: $commentableId, text: $text, gifUrl: $gifUrl }
    ) {
      errors {
        message
      }
      success
      comment {
        ...CompetitionCommentFields
      }
    }
  }
`;

export const COMPETITION_COMMENT_DELETE = gql`
  ${COMPETITION_COMMENT_FIELDS}

  mutation CompetitionCommentDelete($commentId: String!) {
    commentDelete(input: { commentId: $commentId }) {
      errors {
        message
      }
      success
      comment {
        ...CompetitionCommentFields
      }
    }
  }
`;

export const COMPETITION_FIELDS = gql`
  fragment CompetitionFields on Competition {
    id
    _id
    title
    description
    active
    visibility
    sponsored
    approvalStatus
    startsAt
    endsAt
    bannerGraphic
    participantCount
    totalSubmissions
    entryRequirement
    submissionRule
    clipRequirement
    rankingConfig
    prizes
    myEntry {
      id
      competitionId
      userId
      score
      qualified
      clipsUntilQualified
      dailySubmissions {
        limit
        used
        remaining
      }
      submittedClips {
        id
        shareId
        title
        score
        views
        createdAt
        thumbnailUrl(style: STANDARD)
      }
    }
  }
`;

export const GET_CREATOR_COMPETITIONS = gql`
  query GetCreatorCompetitions(
    $status: CompetitionStatus
    $sort: CompetitionSort
    $visibility: String
    $first: Int
    $after: String
  ) {
    creatorCompetitions(
      status: $status
      sort: $sort
      visibility: $visibility
      first: $first
      after: $after
    ) {
      nodes {
        ...CompetitionFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
        totalCount
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_MY_COMPETITIONS = gql`
  query GetMyCompetitions($sort: CompetitionSort, $first: Int, $after: String) {
    myCompetitions(sort: $sort, first: $first, after: $after) {
      nodes {
        ...CompetitionFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
        totalCount
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_COMPETITION = gql`
  query GetCompetition($id: String!) {
    competition(id: $id) {
      ...CompetitionFields
      leaderboard(limit: 10) {
        topEntries {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
        myEntry {
          rank
          score
          submittedClipCount
          qualified
          userId
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const GET_COMPETITION_LEADERBOARD = gql`
  query CompetitionLeaderboard($id: String!, $limit: Int) {
    competitionLeaderboard(id: $id, limit: $limit) {
      topEntries {
        rank
        score
        submittedClipCount
        qualified
        userId
        user {
          id
          username
          avatarUrl
          userGroup {
            type
          }
        }
      }
      myEntry {
        rank
        score
        submittedClipCount
        qualified
        userId
        user {
          id
          username
          avatarUrl
          userGroup {
            type
          }
        }
      }
    }
  }
`;

export const GET_ELIGIBLE_CLIPS = gql`
  query GetEligibleClips(
    $competitionId: String!
    $sort: ClipSort
    $first: Int
    $after: String
  ) {
    eligibleClips(
      competitionId: $competitionId
      sort: $sort
      first: $first
      after: $after
    ) {
      nodes {
        id
        shareId
        title
        score
        createdAt
        thumbnailUrl(style: STANDARD)
        tags {
          key
          value
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        totalCount
      }
    }
  }
`;

export const GET_MY_COMPETITION_ENTRY = gql`
  query GetMyCompetitionEntry($competitionId: String!) {
    myCompetitionEntry(competitionId: $competitionId) {
      id
      competitionId
      userId
      score
      qualified
      clipsUntilQualified
      dailySubmissions {
        limit
        used
        remaining
      }
      submittedClips {
        id
        shareId
        title
        score
        views
        createdAt
        thumbnailUrl(style: STANDARD)
      }
    }
  }
`;

export const GET_COMPETITION_FEED = gql`
  query GetCompetitionFeed(
    $id: String!
    $first: Int
    $after: String
    $sort: CompetitionFeedSort
    $submitterId: String
  ) {
    competitionFeed(
      id: $id
      first: $first
      after: $after
      sort: $sort
      submitterId: $submitterId
    ) {
      nodes {
        id
        submittedAt
        clip {
          id
          shareId
          title
          score
          views
          createdAt
          game
          matchId
          status
          thumbnailUrl(style: STANDARD)
          videoUrl: url(orientation: LANDSCAPE)
          tags {
            key
            value
          }
          user {
            id
            username
            avatarUrl
            userGroup {
              type
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const COMPETITION_CREATE = gql`
  mutation CompetitionCreate($input: CompetitionCreateInput!) {
    competitionCreate(input: $input) {
      competition {
        ...CompetitionFields
      }
      errors {
        field
        code
        message
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const COMPETITION_JOIN = gql`
  mutation CompetitionJoin($input: CompetitionJoinInput!) {
    competitionJoin(input: $input) {
      competitionEntry {
        id
        competitionId
        userId
        score
        submittedClips {
          id
          shareId
          title
          score
          views
          createdAt
          thumbnailUrl(style: STANDARD)
        }
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_UPDATE = gql`
  mutation CompetitionUpdate($input: CompetitionUpdateInput!) {
    competitionUpdate(input: $input) {
      competition {
        ...CompetitionFields
      }
      errors {
        field
        code
        message
      }
    }
  }
  ${COMPETITION_FIELDS}
`;

export const COMPETITION_SUBMIT_CLIPS = gql`
  mutation CompetitionSubmit($input: CompetitionSubmitInput!) {
    competitionSubmit(input: $input) {
      competitionSubmissions {
        id
        clipId
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_DISQUALIFY = gql`
  mutation CompetitionDisqualify($input: CompetitionDisqualifyInput!) {
    competitionDisqualify(input: $input) {
      competitionEntry {
        id
        userId
        disqualified
        disqualificationReason
      }
      errors {
        field
        code
        message
      }
    }
  }
`;

export const COMPETITION_REMOVE_CLIP = gql`
  mutation CompetitionRemoveClip($input: CompetitionRemoveClipInput!) {
    competitionRemoveClip(input: $input) {
      success
    }
  }
`;

export const GET_COMPETITION_PRIZE_WINNERS = gql`
  query GetCompetitionPrizeWinners($id: String!) {
    competition(id: $id) {
      id
      prizeWinners {
        placement
        prizeType
        tier
        duration
        description
        winner {
          userId
          username
          email
          currentTier
        }
      }
    }
  }
`;
